import type { JSX } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { subDays, startOfDay, endOfDay } from "date-fns";
import { Download, RefreshCw, SlidersHorizontal } from "lucide-react";
import type { AnalyticsFieldKey, SegmentDefinition } from "@adlywise/shared";
import {
  createDefaultAnalyticsConfig,
  pruneFilterForConfig,
  type AnalyticsConfig,
} from "@/features/analytics/components/ConfigurePanel";
import { AnalyticsConfigDrawer } from "@/features/analytics/components/AnalyticsConfigDrawer";
import { AggregatedResultsTable } from "@/features/analytics/components/AggregatedResultsTable";
import { RunSummary } from "@/features/analytics/components/RunSummary";
import { useAnalyticsAggregate } from "@/features/analytics/hooks/useAnalyticsAggregate";
import { useAnalyticsNameMaps } from "@/features/analytics/hooks/useAnalyticsNameMaps";
import {
  buildAnalyticsWorkbookSheets,
  describeRun,
  filterNameRows,
  workbookFileName,
} from "@/features/analytics/utils/runReport";
import { downloadXlsx } from "@/features/analytics/utils/downloadXlsx";
import { usePageTitle } from "@/components/common/AppShell";

type PickerRange = { from: Date; to: Date };

const defaultRange = (): PickerRange => {
  const to = endOfDay(new Date());
  const from = startOfDay(subDays(new Date(), 6));
  return { from, to };
};

const EMPTY_FILTER: SegmentDefinition = { all: [] };

const TOOLBAR_BUTTON =
  "inline-flex items-center gap-2 rounded-(--radius-md) border border-(--border-strong) bg-(--surface-1) px-[14px] py-[9px] text-[13px] font-semibold text-(--text-primary) transition-colors hover:border-(--brand-primary) disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-(--border-strong)";

// The results take the full page width. Above them: the submitted run's
// settings and the Download / Configure buttons (Configure rightmost). The
// configuration lives in a drawer, open on arrival (nothing has run yet) and
// closed by Run, so the results it produced are what the user sees next.
const AnalyticsPage = (): JSX.Element => {
  usePageTitle("Analytics");
  const [config, setConfig] = useState<AnalyticsConfig>(
    createDefaultAnalyticsConfig,
  );
  const [pickerRange, setPickerRange] = useState<PickerRange>(defaultRange);
  const [filter, setFilter] = useState<SegmentDefinition>(EMPTY_FILTER);
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const configureButtonRef = useRef<HTMLButtonElement | null>(null);

  const {
    rows,
    truncated,
    moneyLabel,
    hasRun,
    resultConfig: submittedConfig,
    resultRequest,
    isStale,
    isLoading,
    isRunReportButtonDisabled,
    isRequestCancelable,
    runReportButtonLabel,
    errorMessage,
    handleRunReportButtonClick,
  } = useAnalyticsAggregate({ config, range: pickerRange, filter });

  // The submitted run's config, with the LIVE column order (metric order and
  // where Time sits): column order is presentation only (it changes no request), so a reorder in Columns
  // shows at once instead of waiting for a run nothing would flag as needed.
  const resultConfig = useMemo(
    () =>
      submittedConfig && {
        ...submittedConfig,
        metricOrder: config.metricOrder,
        timeColumn: config.timeColumn,
      },
    [submittedConfig, config.metricOrder, config.timeColumn],
  );

  // One name lookup for everything describing the run: the table's group
  // columns, the summary's filter values and the export.
  const nameSource = useMemo(() => {
    const filterNames = filterNameRows(resultRequest);
    const keys: AnalyticsFieldKey[] = [
      ...new Set([...(resultConfig?.groupBy ?? []), ...filterNames.keys]),
    ];
    return { rows: [...rows, ...filterNames.rows], keys };
  }, [rows, resultConfig, resultRequest]);
  const resolution = useAnalyticsNameMaps(nameSource.rows, nameSource.keys);

  const run = useMemo(
    () =>
      resultConfig && resultRequest
        ? describeRun(resultConfig, resultRequest, resolution.names)
        : null,
    [resultConfig, resultRequest, resolution.names],
  );

  // Closing the drawer hands focus back to Configure (skipped on the first
  // render, when the drawer opens on arrival).
  const wasDrawerOpenRef = useRef(isDrawerOpen);
  useEffect(() => {
    if (wasDrawerOpenRef.current && !isDrawerOpen) {
      configureButtonRef.current?.focus();
    }
    wasDrawerOpenRef.current = isDrawerOpen;
  }, [isDrawerOpen]);

  // Any config change can narrow what the report can filter by -- switching
  // source, or selecting a counter -- and the filter is not part of `config`,
  // so it is pruned here in the same gesture. Runs on every change rather than
  // only on the two that narrow, because deciding which ones those are is the
  // kind of list that goes stale the moment a dimension is added.
  const handleConfigChange = (next: AnalyticsConfig): void => {
    setConfig(next);
    setFilter((current) => pruneFilterForConfig(current, next));
  };

  // Run closes the drawer to show what it produces; a click that cancels (the
  // drawer reopened mid-request) leaves it open.
  const handleDrawerRun = (): void => {
    const startsRun = !isLoading;
    handleRunReportButtonClick();
    if (startsRun) setIsDrawerOpen(false);
  };

  // Download waits for the names too: a workbook built while they are still
  // loading would keep the raw ids for good (the summary catches up, a file
  // does not). Ids that cannot be resolved at all -- a deleted entity, a failed
  // lookup -- are exported as ids.
  const canDownload =
    !isLoading &&
    !resolution.isResolving &&
    resultConfig !== null &&
    resultRequest !== null &&
    rows.length > 0;
  const [exportError, setExportError] = useState("");

  const handleDownload = (): void => {
    if (!resultConfig || !resultRequest) return;
    setExportError("");
    const sheets = buildAnalyticsWorkbookSheets({
      config: resultConfig,
      request: resultRequest,
      rows,
      names: resolution.names,
    });
    downloadXlsx(sheets, workbookFileName(resultRequest)).catch((error) => {
      console.error("analytics xlsx export failed", error);
      setExportError("The download failed. Try again.");
    });
  };

  return (
    <section className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 pt-1">
          <RunSummary run={run} onOpenConfig={() => setIsDrawerOpen(true)} />
        </div>
        <div className="flex items-center gap-2">
          {/* The drawer is closed while a run is in flight, so its cancel
              lives here too. */}
          {isRequestCancelable && (
            <button
              type="button"
              onClick={handleRunReportButtonClick}
              className="inline-flex items-center gap-2 rounded-(--radius-md) bg-(--status-danger) px-[14px] py-[9px] text-[13px] font-bold text-white hover:opacity-90"
            >
              Cancel request
            </button>
          )}
          <button
            type="button"
            onClick={handleDownload}
            disabled={!canDownload}
            title={
              resolution.isResolving
                ? "Loading names for the export…"
                : undefined
            }
            className={TOOLBAR_BUTTON}
          >
            <Download
              size={15}
              strokeWidth={1.8}
              className="text-(--brand-primary)"
            />
            Download
          </button>
          <button
            ref={configureButtonRef}
            type="button"
            onClick={() => setIsDrawerOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={isDrawerOpen}
            className={TOOLBAR_BUTTON}
          >
            <SlidersHorizontal
              size={15}
              strokeWidth={1.8}
              className="text-(--brand-primary)"
            />
            Configure
          </button>
        </div>
      </div>

      {exportError && (
        <p role="alert" className="text-[12.5px] text-(--status-danger)">
          {exportError}
        </p>
      )}

      {isStale && !isLoading && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-2) px-4 py-2.5 text-[12.5px] text-(--text-secondary)"
        >
          <RefreshCw size={14} className="shrink-0 text-(--brand-primary)" />
          Settings changed — run the report to update these results.
        </div>
      )}

      {/* Rendered from the config of the run that produced the rows (bar the
          column order, above), never the live one: see resultConfig in
          useAnalyticsAggregate. */}
      <AggregatedResultsTable
        rows={rows}
        config={resultConfig ?? config}
        truncated={truncated}
        moneyLabel={moneyLabel}
        hasRun={hasRun}
        isLoading={isLoading}
        errorMessage={errorMessage}
        resolution={resolution}
      />

      <AnalyticsConfigDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        config={config}
        onConfigChange={handleConfigChange}
        range={pickerRange}
        onRangeChange={setPickerRange}
        filter={filter}
        onFilterChange={setFilter}
        runReportButtonLabel={runReportButtonLabel}
        isRequestCancelable={isRequestCancelable}
        isRunReportButtonDisabled={isRunReportButtonDisabled}
        onRunReportButtonClick={handleDrawerRun}
      />
    </section>
  );
};

export default AnalyticsPage;
